import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MOCK_PROJECTS } from "../lib/mockData";
import { HumaTekFooter } from "../components/layout/HumaTekFooter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatCurrency, getStatusColor } from "../lib/utils";
import { Search, MapPin, Heart, Calendar, Users, ArrowUpRight } from "lucide-react";

export default function ProjectsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const allProjects = MOCK_PROJECTS;

  const filteredProjects = allProjects.filter((project) => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 bg-[#F4F5F7]" data-testid="projects-page">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-slate-800 mb-2">Humanitarian Projects</h1>
          <p className="text-slate-500">Browse and support verified humanitarian projects worldwide</p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search projects by name, country..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-slate-200" data-testid="project-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] border-slate-200" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="funded">Fully Funded</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-2xl font-heading font-bold text-primary">{allProjects.length}</p><p className="text-xs text-slate-500">Total Projects</p></CardContent></Card>
          <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-2xl font-heading font-bold text-primary">{allProjects.filter(p => p.status === 'active').length}</p><p className="text-xs text-slate-500">Active</p></CardContent></Card>
          <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-2xl font-heading font-bold text-green-600">{allProjects.filter(p => p.status === 'completed').length}</p><p className="text-xs text-slate-500">Completed</p></CardContent></Card>
          <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-2xl font-heading font-bold text-primary">{new Set(allProjects.map(p => p.country)).size}</p><p className="text-xs text-slate-500">Countries</p></CardContent></Card>
        </div>

        {/* Project Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => {
            const progress = (project.current_amount / project.target_amount) * 100;
            return (
              <motion.div key={project.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                <Link to={`/projects/${project.id}`} data-testid={`project-card-${index}`}>
                  <Card className="bg-white h-full hover:shadow-md hover:border-primary/40 transition-all group cursor-pointer">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-lg font-heading group-hover:text-primary transition-colors line-clamp-1">{project.title}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" />{project.country}</CardDescription>
                        </div>
                        <Badge variant="outline" className={`shrink-0 text-xs ${getStatusColor(project.status)}`}>{project.status?.replace("_", " ")}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-slate-500 line-clamp-2">{project.description}</p>
                      <div>
                        <div className="flex justify-between text-sm mb-2"><span className="font-medium">{formatCurrency(project.current_amount)}</span><span className="text-slate-400">of {formatCurrency(project.target_amount)}</span></div>
                        <Progress value={progress} className="h-2" />
                        <p className="text-xs text-slate-400 mt-1 text-right">{progress.toFixed(0)}% funded</p>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-400 pt-2 border-t border-slate-100">
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{project.beneficiary_count?.toLocaleString()} beneficiaries</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{project.milestones?.length || 0} milestones</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-20"><Heart className="h-12 w-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500">No projects found matching your criteria</p></div>
        )}
      </div>
      <div className="mt-12"><HumaTekFooter /></div>
    </div>
  );
}
